import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '../lib/api'
import { ProductImage } from './ProductImage'
import type { CatalogSearchResult } from '@shared/types'

interface CatalogPickerModalProps {
  isOpen: boolean
  onClose: () => void
  onSelect: (item: CatalogSearchResult) => void
  onCustomItem: () => void
}

interface AddToCartForm {
  globalProductId: string
  cost: number
  sellPrice: number
  preferredSupplier: string
}

export function CatalogPickerModal({ isOpen, onClose, onSelect, onCustomItem }: CatalogPickerModalProps) {
  const [search, setSearch] = useState('')
  const [tenantResults, setTenantResults] = useState<CatalogSearchResult[]>([])
  const [globalResults, setGlobalResults] = useState<CatalogSearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const [searched, setSearched] = useState(false)
  const [addingGlobal, setAddingGlobal] = useState<AddToCartForm | null>(null)
  const [savingGlobal, setSavingGlobal] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(null)

  const doSearch = useCallback(async (query: string) => {
    setLoading(true)
    try {
      const params = query ? `?search=${encodeURIComponent(query)}&limit=20&includeGlobal=true` : '?limit=20&includeGlobal=false'
      const res = await api.get<{ tenantProducts: CatalogSearchResult[]; globalProducts: CatalogSearchResult[] }>(`/catalog${params}`)
      setTenantResults(res.tenantProducts ?? [])
      setGlobalResults(res.globalProducts ?? [])
    } catch {
      setTenantResults([])
      setGlobalResults([])
    } finally {
      setLoading(false)
      setSearched(true)
    }
  }, [])

  useEffect(() => {
    if (isOpen) {
      setSearch('')
      setTenantResults([])
      setGlobalResults([])
      setSearched(false)
      setAddingGlobal(null)
      doSearch('')
      setTimeout(() => inputRef.current?.focus(), 50)
    }
  }, [isOpen, doSearch])

  function handleSearchChange(value: string) {
    setSearch(value)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => doSearch(value), 300)
  }

  function startAddGlobal(item: CatalogSearchResult) {
    setAddingGlobal({
      globalProductId: item.id,
      cost: 0,
      sellPrice: 0,
      preferredSupplier: '',
    })
  }

  async function handleAddGlobal() {
    if (!addingGlobal) return
    setSavingGlobal(true)
    try {
      const res = await api.post<{ id: string; slug: string }>('/catalog/add-global', {
        globalProductId: addingGlobal.globalProductId,
        cost: addingGlobal.cost,
        sellPrice: addingGlobal.sellPrice,
        msrp: 0,
        preferredSupplier: addingGlobal.preferredSupplier || null,
      })
      // Re-fetch to get the full joined result
      const item = await api.get<CatalogSearchResult>(`/catalog/${res.slug}`)
      if (item) {
        onSelect({ ...item, source: 'tenant' })
      }
      setAddingGlobal(null)
    } catch { /* error */ }
    finally { setSavingGlobal(false) }
  }

  function getItemName(item: CatalogSearchResult) {
    return item.custom_name || item.name || 'Unknown'
  }
  function getItemBrand(item: CatalogSearchResult) {
    return item.custom_brand || item.brand || ''
  }
  function getItemMpn(item: CatalogSearchResult) {
    return item.custom_mpn || item.mpn || ''
  }
  function getItemDescription(item: CatalogSearchResult) {
    return item.custom_description || item.description || ''
  }

  if (!isOpen) return null

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--text)' }}>Add Product from Catalog</h3>
          <button onClick={onClose} style={closeBtnStyle}>&times;</button>
        </div>

        <div style={{ padding: '0 20px 12px' }}>
          <input
            ref={inputRef}
            type="text"
            placeholder="Search by name, manufacturer, MPN, or category..."
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Escape') onClose() }}
            style={searchInputStyle}
          />
        </div>

        <div style={listStyle}>
          {loading && (
            <div style={emptyStyle}>
              <div style={spinnerStyle} />
            </div>
          )}

          {!loading && searched && tenantResults.length === 0 && globalResults.length === 0 && (
            <div style={emptyStyle}>
              <div style={{ fontSize: 14, color: 'var(--text-tertiary)' }}>
                No products found. Try a different search or create a custom item.
              </div>
            </div>
          )}

          {/* YOUR CATALOG section */}
          {!loading && tenantResults.length > 0 && (
            <>
              <div style={sectionHeaderStyle}>YOUR CATALOG</div>
              {tenantResults.map((item) => (
                <button
                  key={item.id}
                  onClick={() => onSelect(item)}
                  style={itemStyle}
                  onMouseOver={(e) => e.currentTarget.style.background = 'var(--bg-hover)'}
                  onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <ProductImage item={{ image_r2_key: item.custom_image_r2_key || item.image_r2_key || null, image_url: item.image_url || null, manufacturer: getItemBrand(item) }} size={44} />
                  <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                    <div style={{ fontSize: 13, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <strong>{getItemBrand(item) || 'Unknown'}</strong>
                      {getItemMpn(item) && <span style={{ color: 'var(--text-secondary)', marginLeft: 6 }}>{getItemMpn(item)}</span>}
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 1 }}>
                      {getItemName(item)}
                    </div>
                    {getItemDescription(item) && (
                      <div style={{ fontSize: 12, color: 'var(--text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 1 }}>
                        {getItemDescription(item).length > 80 ? getItemDescription(item).slice(0, 80) + '...' : getItemDescription(item)}
                      </div>
                    )}
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0, paddingLeft: 12 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', fontFamily: 'var(--font-mono)' }}>
                      ${(item.sell_price || 0).toLocaleString()}
                    </div>
                    {item.cost > 0 && (
                      <div style={{ fontSize: 11, color: 'var(--text-tertiary)', fontFamily: 'var(--font-mono)' }}>
                        cost ${item.cost.toLocaleString()}
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </>
          )}

          {/* PRODUCT LIBRARY section */}
          {!loading && globalResults.length > 0 && (
            <>
              <div style={sectionHeaderStyle}>PRODUCT LIBRARY</div>
              {globalResults.map((item) => {
                const isAdding = addingGlobal?.globalProductId === item.id

                return (
                  <div key={item.id}>
                    <div
                      style={{ ...itemStyle, cursor: isAdding ? 'default' : 'pointer' }}
                      onMouseOver={(e) => { if (!isAdding) e.currentTarget.style.background = 'var(--bg-hover)' }}
                      onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      <ProductImage item={{ image_r2_key: item.image_r2_key || null, image_url: item.image_url || null, manufacturer: item.brand || item.name }} size={44} />
                      <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                        <div style={{ fontSize: 13, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          <strong>{item.brand || 'Unknown'}</strong>
                          {item.mpn && <span style={{ color: 'var(--text-secondary)', marginLeft: 6 }}>{item.mpn}</span>}
                        </div>
                        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 1 }}>
                          {item.name}
                        </div>
                        {item.is_verified === 1 && (
                          <span style={{ fontSize: 10, color: 'var(--success)', fontWeight: 600 }}>VERIFIED</span>
                        )}
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); startAddGlobal(item) }}
                        style={{
                          padding: '6px 12px', fontSize: 12, fontWeight: 500,
                          border: '1px solid var(--primary)', borderRadius: 'var(--radius, 8px)',
                          background: 'transparent', color: 'var(--primary)',
                          cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0,
                        }}
                      >
                        Add to Catalog
                      </button>
                    </div>

                    {/* Inline pricing form */}
                    {isAdding && (
                      <div style={{ padding: '8px 20px 12px', borderBottom: '1px solid var(--border-light, var(--border))', background: 'var(--bg-inset)' }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                          <div>
                            <label style={inlineLabelStyle}>Cost ($)</label>
                            <input
                              type="number" step="0.01" value={addingGlobal.cost}
                              onChange={(e) => setAddingGlobal({ ...addingGlobal, cost: parseFloat(e.target.value) || 0 })}
                              style={{ ...inlineInputStyle, width: 100 }}
                              autoFocus
                            />
                          </div>
                          <div>
                            <label style={inlineLabelStyle}>Sell Price ($)</label>
                            <input
                              type="number" step="0.01" value={addingGlobal.sellPrice}
                              onChange={(e) => setAddingGlobal({ ...addingGlobal, sellPrice: parseFloat(e.target.value) || 0 })}
                              style={{ ...inlineInputStyle, width: 100 }}
                            />
                          </div>
                          <div>
                            <label style={inlineLabelStyle}>Supplier</label>
                            <input
                              type="text" value={addingGlobal.preferredSupplier}
                              onChange={(e) => setAddingGlobal({ ...addingGlobal, preferredSupplier: e.target.value })}
                              style={{ ...inlineInputStyle, width: 140 }}
                              placeholder="Optional"
                            />
                          </div>
                          <button
                            onClick={handleAddGlobal}
                            disabled={savingGlobal}
                            style={{
                              padding: '7px 14px', fontSize: 12, fontWeight: 600,
                              border: 'none', borderRadius: 'var(--radius, 8px)',
                              background: 'var(--primary)', color: '#fff',
                              cursor: 'pointer', fontFamily: 'inherit',
                            }}
                          >
                            {savingGlobal ? 'Saving...' : 'Save & Add to Proposal'}
                          </button>
                          <button
                            onClick={() => setAddingGlobal(null)}
                            style={{
                              padding: '7px 10px', fontSize: 12, fontWeight: 500,
                              border: '1px solid var(--border)', borderRadius: 'var(--radius, 8px)',
                              background: 'transparent', color: 'var(--text-secondary)',
                              cursor: 'pointer', fontFamily: 'inherit',
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </>
          )}
        </div>

        <div style={footerStyle}>
          <button onClick={onCustomItem} style={customBtnStyle}>
            + Create Custom Product
          </button>
        </div>
      </div>
    </div>
  )
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed', inset: 0,
  background: 'var(--bg-overlay, rgba(0,0,0,0.5))',
  backdropFilter: 'blur(4px)',
  zIndex: 100, display: 'flex',
  alignItems: 'center', justifyContent: 'center',
  padding: 24,
}

const modalStyle: React.CSSProperties = {
  background: 'var(--bg-surface)',
  borderRadius: 'var(--radius-xl, 16px)',
  boxShadow: 'var(--shadow-lg)',
  maxWidth: 640, width: '100%',
  maxHeight: '80vh',
  display: 'flex', flexDirection: 'column',
  animation: 'modal-in 0.2s ease',
}

const headerStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '20px 20px 12px',
}

const closeBtnStyle: React.CSSProperties = {
  background: 'none', border: 'none', fontSize: 22,
  color: 'var(--text-tertiary)', cursor: 'pointer',
  width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
  borderRadius: 'var(--radius, 8px)',
}

const searchInputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 14px',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius, 8px)',
  fontSize: 14, outline: 'none',
  background: 'var(--bg, transparent)',
  color: 'var(--text)',
  fontFamily: 'inherit',
}

const listStyle: React.CSSProperties = {
  flex: 1, overflowY: 'auto',
  minHeight: 200, maxHeight: 'calc(80vh - 200px)',
}

const emptyStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: 40,
}

const spinnerStyle: React.CSSProperties = {
  width: 24, height: 24, borderRadius: '50%',
  border: '2px solid var(--border)',
  borderTopColor: 'var(--primary, var(--info))',
  animation: 'spin 0.6s linear infinite',
}

const sectionHeaderStyle: React.CSSProperties = {
  padding: '10px 20px 6px',
  fontSize: 11, fontWeight: 600,
  color: 'var(--text-tertiary)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  borderBottom: '1px solid var(--border-light, var(--border))',
}

const itemStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 12,
  width: '100%', padding: '10px 20px',
  border: 'none', background: 'transparent',
  cursor: 'pointer', textAlign: 'left',
  borderBottom: '1px solid var(--border-light, var(--border))',
  fontFamily: 'inherit', transition: 'background 0.1s',
}

const footerStyle: React.CSSProperties = {
  padding: '12px 20px',
  borderTop: '1px solid var(--border)',
}

const customBtnStyle: React.CSSProperties = {
  width: '100%', padding: '10px 0',
  border: '1px dashed var(--border)',
  borderRadius: 'var(--radius, 8px)',
  background: 'transparent',
  color: 'var(--text-secondary)',
  fontSize: 13, fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'inherit',
}

const inlineLabelStyle: React.CSSProperties = {
  fontSize: 11, fontWeight: 500, color: 'var(--text-secondary)',
  display: 'block', marginBottom: 2,
}

const inlineInputStyle: React.CSSProperties = {
  padding: '6px 8px', fontSize: 13,
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius, 8px)',
  background: 'var(--bg, transparent)',
  color: 'var(--text)',
  fontFamily: 'inherit',
  outline: 'none',
}
